import { defineStore } from "pinia";
import VmsService from "../services/Vms";

// Store Pinia do zarządzania stanem danych VMS (Variable Message Signs)
export const useVmsStore = defineStore({
  id: "vms",
  state: () => ({
    groupStatuses: {},      // Statusy wszystkich grup znaków VMS (np. czy włączone/wyłączone)
    groupDescriptions: {},  // Opisy grup VMS (np. nazwy, lokalizacje)
    groupLocations: {},     // Stacje preselekcji, do których należą grupy (do filtrowania listy)
    isLoaded: false,        // Flaga - czy dane zostały już załadowane
    isLoading: false,       // Flaga - czy obecnie trwa ładowanie danych
  }),

  actions: {
    /**
     * Pobiera statusy i opisy grup VMS z backendu.
     *
     * Domyślnie pobiera dane tylko raz na sesję aplikacji - kolejne wejścia na zakładkę korzystają z tego, co już
     * jest w pamięci. Świadome odświeżenie (przycisk w interfejsie) przekazuje `force`, a `fresh` dodatkowo wymusza
     * odpytanie samych tablic zamiast odczytu ostatniego znanego stanu.
     *
     * @param {Object} options
     * @param {Boolean} options.force Pomija bramkę `isLoaded` i pobiera dane ponownie.
     * @param {Boolean} options.fresh Wymusza odpytanie znaków na żywo.
     */
    async loadData({ force = false, fresh = false } = {}) {
      // Zabezpieczenie przed wielokrotnym ładowaniem. Równoległe wywołanie zawsze jest odrzucane - także przy
      // `force`, bo dwa jednoczesne pobrania i tak dałyby ten sam wynik.
      // DEBUG VMS
      if (this.isLoading || (this.isLoaded && !force)) {
        return;
      }

      this.isLoading = true;
      try {
        // Pobierz statusy, opisy i stacje grup VMS z serwisu
        const result = await VmsService.getAllGroupStatus({ fresh });
        // UWAGA: przy błędzie serwis zwraca {} (nie tablicę) - destrukturyzacja niżej rzuci "is not iterable".
        const [statuses, descriptions, locations] = result;
        this.groupStatuses = statuses;
        this.groupDescriptions = descriptions;
        this.groupLocations = locations || {};
        this.isLoaded = true; // Oznacz jako załadowane
      } catch (error) {
        // Obsługa błędu - wyświetl w konsoli
        console.error("Błąd podczas ładowania danych VMS:", error);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Odświeża stan znaków w jednej grupie.
     *
     * Wywoływane po operacji sterowania. Dotyka wyłącznie tej grupy, której operacja dotyczyła, więc jest
     * wielokrotnie tańsze niż pobranie stanu wszystkich tablic w systemie.
     *
     * @param {Number|String} groupId Identyfikator grupy.
     * @param {Object} options
     * @param {Boolean} options.fresh Wymusza odpytanie znaków na żywo (domyślnie tak - po operacji interesuje nas
     *                                stan faktyczny, nie ten sprzed kilkudziesięciu sekund).
     *
     * @returns {Promise<Boolean>} TRUE, gdy udało się odświeżyć dane.
     */
    async refreshGroup(groupId, { fresh = true } = {}) {
      try {
        // DEBUG VMS
        const group = await VmsService.getGroupStatus(groupId, { fresh });

        if (!group?.group) {
          return false;
        }

        // Podmiana całych map wymusza reaktywność także wtedy, gdy klucz grupy już istniał.
        this.groupStatuses = { ...this.groupStatuses, [group.group.id]: group.vms };
        this.groupDescriptions = { ...this.groupDescriptions, [group.group.id]: group.group.description };
        this.groupLocations = { ...this.groupLocations, [group.group.id]: group.group.location || null };

        return true;
      } catch (error) {
        console.error("Błąd podczas odświeżania grupy VMS:", error);
        return false;
      }
    },
  },
});

// Obsługa Hot Module Replacement dla Pinia (tylko podczas developmentu)
// if (typeof import.meta !== 'undefined' && import.meta.hot) {
//   import.meta.hot.accept(acceptHMRUpdate(useVmsStore, import.meta.hot));
// }
